import React from "react";
import { arrayUniqueByKey } from "lib/utils";
import { features } from "app/features";
import { useIsRedesign } from "pages/redesign/preferences/redesignUtils";
import {
  GRP_JAPI_ANALYST_AUTOCOMPLETE_URL,
  GRP_JAPI_COMPANY_AUTOCOMPLETE_URL,
  GRP_JAPI_COUNTRY_AUTOCOMPLETE_URL,
  GRP_JAPI_PERIODICALS_AUTOCOMPLETE_URL,
  GRP_JAPI_REGION_AUTOCOMPLETE_URL,
} from "../../advancedSearch/parts/useSuggestions";
import { useFetch } from "../../../lib/useFetch";
import { LoadingSpinner } from "../../../lib/Spinner";

const urlMap = {
  Analyst: GRP_JAPI_ANALYST_AUTOCOMPLETE_URL,
  Company: GRP_JAPI_COMPANY_AUTOCOMPLETE_URL,
  Country: GRP_JAPI_COUNTRY_AUTOCOMPLETE_URL,
  Region: GRP_JAPI_REGION_AUTOCOMPLETE_URL,
  "Analyst-JA": GRP_JAPI_ANALYST_AUTOCOMPLETE_URL,
  "Company-JA": GRP_JAPI_COMPANY_AUTOCOMPLETE_URL,
  "Report Titles": GRP_JAPI_PERIODICALS_AUTOCOMPLETE_URL,
  "Report Titles-JA": GRP_JAPI_PERIODICALS_AUTOCOMPLETE_URL,
};
export const GRP_JAPI_AUTOCOMPLETE_URL = `/research/japi/pub/search/autocomplete/`;

// needs two separate functions as condition must be checked before useFetch
export function JAPIAutoCompleteLoader(props) {
  return props.keyword || props.keyword.length > 2 ? <DoAutoCompleteLoaderJAPI {...props} /> : null;
}

export function DoAutoCompleteLoaderJAPI({ keyword = "", language, render, category = "", ...props }) {
  const url = category ? urlMap[category] || GRP_JAPI_AUTOCOMPLETE_URL : GRP_JAPI_AUTOCOMPLETE_URL;
  const body = JSON.stringify({ filter: keyword, ...{ language } });
  const [data, isLoading] = useFetch(url, body);

  const { setIsSearching = () => {} } = props;
  const isRedesign = useIsRedesign();

  if (!keyword) return render([], []);
  if (isLoading) {
    if (isRedesign) {
      setIsSearching(true);
    } else return <LoadingSpinner />;
  } else setIsSearching(false);

  if (!(data && data.suggest && data.suggest.autocomplete && data.suggest.autocomplete[0].options)) return null; // TODO: 500 err handling

  const suggestions = parseSuggestions(data, props);
  return render(suggestions, data);
}

const parseSuggestions = (data, props) => {
  let suggestions = dataToSuggestions(data);
  sortAutoCompleteItems(suggestions);
  suggestions = addHeaders(suggestions); // add header items to autocomplete array

  if (features.dedupAutoCompleteSuggestions && props.dedupAutocompleteSuggestions) {
    suggestions = arrayUniqueByKey(suggestions, "CategoryValue");
  }
  return suggestions;
};

const dataToSuggestions = (data) =>
  data.suggest.autocomplete[0].options.map((el) => ({
    CategoryName: parseCategory(el._source.category),
    CategoryValue: el._source.name,
    CategoryLang: el._source.language,
    ticker: el._source.ticker,
  }));

const parseCategory = (category) => {
  const categoryMap = {
    company: "CompanyWithTicker",
    analyst: "Analyst",
    periodical: "Report Titles",
    theme_subtheme: "Theme",
    country: "Country",
    region: "Region",
  };
  return categoryMap[category] || category;
};

export const sortAutoCompleteItems = (suggestions) =>
  suggestions.sort((a, b) => {
    const priorityTop1st = "analyst";
    const priorityTop2nd = "theme";
    const nameA = a.CategoryName.toLowerCase();
    const nameB = b.CategoryName.toLowerCase();
    if (nameA === nameB) {
      return 0;
    }
    if (nameA === priorityTop1st) {
      return -1;
    }
    if (nameB === priorityTop1st) {
      return 1;
    }
    if (nameA === priorityTop2nd) {
      return -2;
    }
    if (nameB === priorityTop2nd) {
      return 2;
    }
    if (nameA < nameB)
      // sort string desc
      return -1;
    if (nameA > nameB) return 1;
    return 0;
  });

export const addHeaders = (suggestions, _prevHeader = "") =>
  suggestions.reduce((acc, suggestion) => {
    const header = getHeader(suggestion);
    if (header && _prevHeader !== header) acc.push({ CategoryName: header, CategoryValue: header });
    acc.push(suggestion);
    _prevHeader = header;
    return acc;
  }, []);

const getHeader = (suggestion) =>
  suggestion.CategoryName === "Analyst"
    ? "Analysts"
    : suggestion.CategoryName === "CompanyWithTicker"
      ? "Companies"
      : suggestion.CategoryName === "Report Titles"
        ? "Periodicals"
        : suggestion.CategoryName === "Theme"
          ? "Themes"
          : "";
